import { User } from 'src/shared/types/user';

import { MIXPANEL_TOKEN } from '../../settings';
import { info } from '../logger';
import { MixpanelProvider } from './providers/mixpanel.provider';
import { AnalyticsProvider } from './providers/types';

// Add this switch for debugging because this can be very noisy.
const SILENCED_LOGGING = true;

const testingUsers = ['notstaff', 'preview', 'fullfeatured'];

/**
 * Singleton facade that delegates to the active AnalyticsProvider.
 *
 * Swap the provider by calling `setProvider` with a new implementation before
 * `initProvider` is called (e.g. at app bootstrap).
 *
 * Example – switching to Amplitude:
 *   import { AmplitudeProvider } from './providers/amplitude.provider';
 *   analyticsService.setProvider(new AmplitudeProvider());
 */
class AnalyticsService {
  private provider: AnalyticsProvider = new MixpanelProvider();

  /** Replace the active provider. Call before `initProvider`. */
  setProvider(provider: AnalyticsProvider): void {
    this.provider = provider;
  }

  /**
   * Initialise the provider and optionally identify the current user.
   * Mirrors the same staff/impersonation guards used by the existing GA4 service.
   */
  initProvider({ userInfo }: { userInfo?: User | null }): void {
    if (!MIXPANEL_TOKEN) {
      return info('[AnalyticsService] token not configured, skipping init');
    }

    if (!userInfo) {
      return info('[AnalyticsService] no user info, skipping init');
    }

    const { username, isStaff, impersonating } = userInfo;
    const shouldTrack =
      !isStaff &&
      !impersonating &&
      username &&
      !testingUsers.includes(username);

    if (!shouldTrack) {
      return info(
        `[AnalyticsService] tracking disabled - isStaff: ${isStaff}, impersonating: ${JSON.stringify(impersonating)}, testingUser: ${testingUsers.includes(username)}`
      );
    }

    this.provider.init(MIXPANEL_TOKEN);
    this.provider.identify(username, { email: userInfo.email });

    if (!SILENCED_LOGGING) {
      info(`[AnalyticsService] provider initialised for user: ${username}`);
    }
  }

  /** Send a named event with optional properties. */
  trackEvent(event: string, properties?: Record<string, unknown>): void {
    this.provider.track(event, properties);
    !SILENCED_LOGGING &&
      info(`[AnalyticsService] tracked: ${event}`, properties);
  }

  /** Reset user identity (call on logout). */
  reset(): void {
    this.provider.reset();
  }
}

export const analyticsService = new AnalyticsService();
